<template>
  <div class="page-container">
    <div class="page-title">{{ $t(isEdit ? 'operation.edit_franchisee' : 'operation.create_franchisee') }}</div>
    <el-form ref="form" v-loading="loading" :model="form" :rules="rules" label-width="170px" class="form-content">
      <el-form-item v-if="isEdit" :label="$t('operation.franchisee_no')">
        <el-input v-model="form.franchisee_no" disabled />
      </el-form-item>
      <el-form-item :label="$t('operation.franchisee_name')" prop="franchisee_name">
        <el-input v-model="form.franchisee_name" maxlength="128" show-word-limit :placeholder="$t('operation.enter_franchisee_name')" />
      </el-form-item>
      <el-form-item :label="$t('operation.region')" prop="country_code">
        <el-select v-model="form.country_code" :disabled="isEdit" :loading="regionLoading" :placeholder="$t('operation.select_region')" style="width: 100%" @change="onRegionChange">
          <el-option v-for="region in regionOptions" :key="region.value" :label="region.label" :value="region.value" />
        </el-select>
      </el-form-item>
      <el-form-item :label="$t('operation.currency')" prop="currency">
        <el-select v-model="form.currency" :disabled="isEdit || !form.country_code" :placeholder="$t('operation.select_currency')" style="width: 100%">
          <el-option v-for="currencyOption in currencyOptions" :key="currencyOption" :label="currencyOption" :value="currencyOption" />
        </el-select>
      </el-form-item>
      <el-form-item :label="$t('operation.remark')" prop="remark">
        <el-input v-model="form.remark" maxlength="128" show-word-limit :placeholder="$t('operation.enter_remark')" />
      </el-form-item>
      <el-form-item :label="$t('operation.contact_name')" prop="contact_name">
        <el-input v-model="form.contact_name" maxlength="32" show-word-limit :placeholder="$t('operation.enter_contact_name')" />
      </el-form-item>
      <el-form-item :label="$t('operation.contact_phone')" prop="contact_phone">
        <el-input v-model="form.contact_phone" maxlength="16" show-word-limit :placeholder="$t('operation.enter_phone')" />
      </el-form-item>
      <el-form-item :label="$t('operation.contact_email')" prop="contact_email">
        <el-input v-model="form.contact_email" maxlength="32" show-word-limit :placeholder="$t('operation.enter_email')" />
      </el-form-item>
      <el-form-item :label="$t('operation.description')" prop="description">
        <el-input v-model="form.description" type="textarea" :rows="4" maxlength="512" show-word-limit :placeholder="$t('operation.enter_description')" />
      </el-form-item>
      <el-form-item>
        <el-button @click="$router.back()">{{ $t('components.cancel') }}</el-button>
        <el-button type="primary" @click="submit">{{ $t('components.confirm') }}</el-button>
      </el-form-item>
    </el-form>
    <el-dialog :visible.sync="confirmDialogVisible" :title="$t('operation.confirm_submit')" width="560px" :close-on-click-modal="false">
      <p class="confirm-description">{{ $t('operation.confirm_franchisee') }}</p>
      <el-descriptions :column="1" border class="confirm-details">
        <el-descriptions-item :label="$t('operation.franchisee_name')">{{ form.franchisee_name }}</el-descriptions-item>
        <el-descriptions-item :label="$t('operation.region')">{{ selectedRegionLabel }}</el-descriptions-item>
        <el-descriptions-item :label="$t('operation.currency')">{{ form.currency }}</el-descriptions-item>
        <el-descriptions-item :label="$t('operation.remark')">{{ form.remark || '-' }}</el-descriptions-item>
        <el-descriptions-item :label="$t('operation.contact_name')">{{ form.contact_name || '-' }}</el-descriptions-item>
        <el-descriptions-item :label="$t('operation.contact_phone')">{{ form.contact_phone || '-' }}</el-descriptions-item>
        <el-descriptions-item :label="$t('operation.contact_email')">{{ form.contact_email || '-' }}</el-descriptions-item>
        <el-descriptions-item :label="$t('operation.description')">{{ form.description || '-' }}</el-descriptions-item>
      </el-descriptions>
      <div slot="footer">
        <el-button @click="confirmDialogVisible = false">{{ $t('components.cancel') }}</el-button>
        <el-button type="primary" :loading="submitting" @click="confirmSubmit">{{ $t('components.confirm') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { createFranchisee, fetchFranchiseeDetail, updateFranchisee } from '@/api/operation-manage/franchisee'
import { fetchOnlineCountryList } from '@/api/components/components.js'

const getDefaultForm = () => ({
  franchisee_no: '',
  franchisee_name: '',
  country_code: '',
  currency: '',
  remark: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  description: ''
})

export default {
  name: 'FranchiseeForm',
  data() {
    return {
      loading: false,
      submitting: false,
      confirmDialogVisible: false,
      regionLoading: false,
      onlineCountryList: [],
      form: getDefaultForm(),
      rules: {
        franchisee_name: [{ required: true, message: this.$t('operation.enter_franchisee_name'), trigger: 'blur' }],
        country_code: [{ required: true, message: this.$t('operation.select_region'), trigger: 'change' }],
        currency: [{ required: true, message: this.$t('operation.select_currency'), trigger: 'change' }]
      }
    }
  },
  computed: {
    isEdit() {
      return this.$route.name === 'Franchisee_Edit'
    },
    regionOptions() {
      const language = this.$store.getters.language
      const currentCountryCode = this.$store.getters.curCountry.country_code
      const availableCountries = currentCountryCode !== 'HK-USD'
        ? this.onlineCountryList.filter(country => country.code === currentCountryCode)
        : this.onlineCountryList
      return availableCountries.map(country => ({
        label: language === 'zh' ? country.cn_name : country.en_name,
        value: country.code
      }))
    },
    selectedRegionLabel() {
      const region = this.regionOptions.find(option => option.value === this.form.country_code)
      return region ? region.label : this.form.country_code
    },
    currencyOptions() {
      const currentCountryCode = this.$store.getters.curCountry.country_code
      if (currentCountryCode === 'HK-USD') {
        return [...new Set(this.onlineCountryList.reduce((currencies, country) => {
          const countryCurrencies = Array.isArray(country.currency)
            ? country.currency
            : String(country.currency || '').split(',')
          return currencies.concat(countryCurrencies.map(currency => currency.trim()).filter(Boolean))
        }, []))]
      }
      const region = this.onlineCountryList.find(country => country.code === this.form.country_code)
      if (!region || !region.currency) return []
      return Array.isArray(region.currency)
        ? region.currency
        : String(region.currency).split(',').map(currencyValue => currencyValue.trim()).filter(Boolean)
    }
  },
  created() {
    this.getOnlineCountryList()
    if (this.isEdit) this.getFranchiseeDetail()
  },
  methods: {
    getOnlineCountryList() {
      this.regionLoading = true
      fetchOnlineCountryList().then(response => {
        this.onlineCountryList = response.data || []
      }).finally(() => {
        this.regionLoading = false
      })
    },
    getFranchiseeDetail() {
      const { franchisee_no } = this.$route.query
      if (!franchisee_no) return
      this.loading = true
      fetchFranchiseeDetail(franchisee_no).then(response => {
        if (response.code === 10000) {
          this.form = response.data
        }
      }).finally(() => {
        this.loading = false
      })
    },
    onRegionChange() {
      this.form.currency = ''
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        if (this.isEdit) {
          this.confirmSubmit()
        } else {
          this.confirmDialogVisible = true
        }
      })
    },
    confirmSubmit() {
      this.submitting = true
      const requestData = {
        ...this.form,
        franchisee_no: this.isEdit ? this.form.franchisee_no : undefined
      }
      if (!this.isEdit) delete requestData.franchisee_no
      const submitRequest = this.isEdit ? updateFranchisee : createFranchisee
      submitRequest(requestData).then(response => {
        if (response.code === 10000) {
          this.confirmDialogVisible = false
          this.$message.success(this.$t(this.isEdit ? 'operation.franchisee_edit_success' : 'operation.franchisee_create_success'))
          this.$router.push({ name: 'Franchisee_Manage_List' })
        }
      }).finally(() => {
        this.submitting = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  margin: 40px 57px 50px 32px;
}
.page-title {
  padding-bottom: 24px;
  border-bottom: 1px solid #D3D7DF;
}
.form-content {
  width: 640px;
  margin-top: 32px;
}
.confirm-description {
  margin: 0 0 20px;
  color: #606266;
  font-size: 14px;
}
.confirm-details {
  margin-bottom: 8px;

  ::v-deep .el-descriptions-item__label {
    width: 170px;
  }
}
</style>
